import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useFetchAxios } from '@/hooks';

export type AnaliseVisitaStatus = 'ocioso' | 'processando' | 'pronto' | 'falha' | 'erro';

interface AnaliseVisitaParams {
  codigo?: number | string | null;
  nroRepresentante?: number | string | null;
}

interface AnaliseVisitaResult {
  html: string | null;
  status: AnaliseVisitaStatus;
  mensagemErro: string | null;
  tentativas: number;
  atualizar: () => void;
  tentarNovamente: () => void;
}

const ENDPOINT = '/rca/analise-cliente';

const INTERVALO_MINIMO_SEGUNDOS = 5;
const INTERVALO_PADRAO_SEGUNDOS = 15;
const TEMPO_MAXIMO_MS = 2 * 60 * 1000;

const MENSAGEM_FALHA = 'Não foi possível gerar a análise deste cliente.';
const MENSAGEM_TIMEOUT = 'A análise está demorando mais do que o esperado.';
const MENSAGEM_ERRO = 'Não foi possível carregar a análise de visita.';

export const useAnaliseVisita = ({ codigo, nroRepresentante }: AnaliseVisitaParams): AnaliseVisitaResult => {
  const { axiosCtx } = useFetchAxios();

  const [html, setHtml] = useState<string | null>(null);
  const [status, setStatus] = useState<AnaliseVisitaStatus>('ocioso');
  const [mensagemErro, setMensagemErro] = useState<string | null>(null);
  const [tentativas, setTentativas] = useState(0);
  const [requisicao, setRequisicao] = useState({ id: 0, forcar: false });

  useEffect(() => {
    if (!codigo || !nroRepresentante) return;

    const controller = new AbortController();
    const iniciadoEm = Date.now();

    let cancelado = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    setHtml(null);
    setMensagemErro(null);
    setTentativas(0);
    setStatus('processando');

    const consultar = async (forcar: boolean) => {
      try {
        const resposta = await axiosCtx.get(ENDPOINT, {
          signal: controller.signal,
          params: {
            codigo,
            nro_representante: nroRepresentante,
            ...(forcar ? { forcar: true } : {}),
          },
        });

        if (cancelado) return;

        setTentativas((total) => total + 1);

        if (resposta.status === 200 && resposta.data?.html) {
          setHtml(resposta.data.html);
          setStatus('pronto');
          return;
        }

        if (resposta.status !== 202) {
          setStatus('erro');
          setMensagemErro(MENSAGEM_ERRO);
          return;
        }

        if (Date.now() - iniciadoEm >= TEMPO_MAXIMO_MS) {
          setStatus('erro');
          setMensagemErro(MENSAGEM_TIMEOUT);
          return;
        }

        // O servidor sabe o quanto falta da geração, o cliente não: o intervalo vem sempre
        // do corpo (`Retry-After` é só espelho) e nunca desce abaixo do piso combinado.
        const segundos = Math.max(
          INTERVALO_MINIMO_SEGUNDOS,
          Number(resposta.data?.retryAposSegundos) || INTERVALO_PADRAO_SEGUNDOS
        );

        // setTimeout encadeado, nunca setInterval: com setInterval as requisições se
        // empilhariam sempre que uma consulta demorasse mais que o intervalo.
        // As repetições vão sem `forcar` — só a primeira chamada do ciclo pode
        // invalidar o cache, senão cada poll dispararia uma geração nova (custo real).
        timeoutId = setTimeout(() => consultar(false), segundos * 1000);
      } catch (erro: any) {
        if (cancelado || axios.isCancel(erro)) return;

        // 502 é falha definitiva da geração. Cada tentativa tem custo monetário
        // (geração via LLM), então não reagenda sozinho: só o botão do usuário retoma.
        if (erro?.response?.status === 502) {
          setStatus('falha');
          setMensagemErro(MENSAGEM_FALHA);
          return;
        }

        setStatus('erro');
        setMensagemErro(MENSAGEM_ERRO);
      }
    };

    void consultar(requisicao.forcar);

    return () => {
      cancelado = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [axiosCtx, codigo, nroRepresentante, requisicao]);

  const atualizar = useCallback(() => setRequisicao((atual) => ({ id: atual.id + 1, forcar: true })), []);
  const tentarNovamente = useCallback(() => setRequisicao((atual) => ({ id: atual.id + 1, forcar: false })), []);

  return { html, status, mensagemErro, tentativas, atualizar, tentarNovamente };
};
